// Fires the Meta custom event "HighIntentAddToCart" (via the Meta sales
// channel's main-frame fbq) once a visitor has added to cart AND then shown
// genuine follow-through. Qualifying signals, any one of which is enough:
//   * 45s of ACTIVE (tab-visible) time after the add — background tabs do not count
//   * clicking a checkout control
//   * landing on the cart page after the add
//   * viewing a different product after the add (continued browsing)
// Fast buyers who add and check out within seconds still qualify via the
// checkout signal, so the strongest intent is never filtered out.
// Never fires the standard AddToCart event, so it cannot duplicate the sales
// channel's reporting. Fires at most once per browser session (per tab).
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;

  var ACTIVE_MS_REQUIRED = 45000;
  var TICK_MS = 1000;

  var KEY_ADDED = 'hiatc:added';
  var KEY_ADD_PATH = 'hiatc:addpath';
  var KEY_ACTIVE = 'hiatc:active';
  var KEY_FIRED = 'hiatc:fired';
  var KEY_PAYLOAD = 'hiatc:payload';

  var storage;
  try {
    storage = window.sessionStorage;
    storage.setItem('hiatc:test', '1');
    storage.removeItem('hiatc:test');
  } catch (e) {
    return;
  }

  // Captured before anything on this page load can set it, so it tells us
  // whether the current page was opened *after* the add to cart.
  var loadedAfterAdd = !!storage.getItem(KEY_ADDED);
  var timer = null;

  function hasAdded() {
    return !!storage.getItem(KEY_ADDED);
  }

  function hasFired() {
    return !!storage.getItem(KEY_FIRED);
  }

  function stopTimer() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function fire(signal) {
    if (hasFired()) return;
    storage.setItem(KEY_FIRED, '1');
    stopTimer();

    var payload = {};
    try {
      payload = JSON.parse(storage.getItem(KEY_PAYLOAD)) || {};
    } catch (e) {
      payload = {};
    }
    // Which condition qualified the visitor — useful for segmenting later.
    payload.intent_signal = signal;

    // fbq is loaded by the Meta sales channel pixel; wait for it if needed.
    var attempts = 0;
    (function send() {
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'HighIntentAddToCart', payload);
      } else if (attempts++ < 40) {
        setTimeout(send, 500);
      }
    })();
  }

  function tick() {
    if (hasFired()) {
      stopTimer();
      return;
    }
    // Time only accrues while the tab is actually in front.
    if (document.visibilityState && document.visibilityState !== 'visible') return;
    var next = (Number(storage.getItem(KEY_ACTIVE)) || 0) + TICK_MS;
    storage.setItem(KEY_ACTIVE, String(next));
    if (next >= ACTIVE_MS_REQUIRED) fire('dwell');
  }

  function startTimer() {
    if (timer || hasFired() || !hasAdded()) return;
    timer = setInterval(tick, TICK_MS);
  }

  function isCartPath(path) {
    return /\/cart(\/|$)/.test(path);
  }

  function isProductPath(path) {
    return path.indexOf('/products/') !== -1;
  }

  // Explicit progression signals available at page load.
  function checkNavigationSignals() {
    if (hasFired() || !loadedAfterAdd) return;
    var path = window.location.pathname;
    if (isCartPath(path)) {
      fire('cart_view');
      return;
    }
    if (isProductPath(path) && path !== storage.getItem(KEY_ADD_PATH)) {
      fire('browsed_on');
    }
  }

  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    'a[href*="/checkout"]',
    '[data-testid="Checkout-button"]'
  ].join(',');

  // Cart links are a weaker signal than a checkout control, so they are
  // tracked separately to keep intent_signal meaningful.
  var CART_SELECTOR = 'a[href$="/cart"], a[href*="/cart?"]';

  document.addEventListener('click', function (event) {
    if (hasFired() || !hasAdded()) return;
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest(CHECKOUT_SELECTOR)) {
      fire('checkout_click');
    } else if (target.closest(CART_SELECTOR)) {
      fire('cart_click');
    }
  }, true);

  function onAddedToCart(responseJson) {
    if (hasFired()) return;

    // Backfill the payload on a later add if the first response was unusable,
    // but never restart the dwell clock — it measures time since the first add.
    if (storage.getItem(KEY_PAYLOAD)) {
      if (hasAdded()) return;
    }

    try {
      var item = responseJson && responseJson.items ? responseJson.items[0] : responseJson;
      if (item && item.id) {
        // Mirrors the field shape the Meta sales channel sends for standard
        // AddToCart: product id in content_ids, variant id kept separate.
        storage.setItem(KEY_PAYLOAD, JSON.stringify({
          content_ids: item.product_id ? [item.product_id] : [item.id],
          content_type: item.product_id ? 'product_group' : 'product',
          content_name: item.product_title || item.title || '',
          content_category: item.product_type || '',
          product_variant_ids: [item.id],
          num_items: item.quantity || 1,
          value: (item.final_price || item.price || 0) / 100,
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'ILS'
        }));
      }
    } catch (e) {
      // payload is optional
    }
    if (!hasAdded()) {
      storage.setItem(KEY_ADDED, String(Date.now()));
      storage.setItem(KEY_ADD_PATH, window.location.pathname);
      storage.setItem(KEY_ACTIVE, '0');
    }
    startTimer();
  }

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url);
    var promise = originalFetch.apply(this, arguments);
    if (isCartAddUrl(url)) {
      promise.then(function (response) {
        if (response.ok) {
          response.clone().json().then(onAddedToCart).catch(function () {
            onAddedToCart(null);
          });
        }
      }).catch(function () {});
    }
    return promise;
  };

  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartAddUrl(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) {
          var json = null;
          try {
            json = JSON.parse(this.responseText);
          } catch (e) {}
          onAddedToCart(json);
        }
      });
    }
    return originalOpen.apply(this, arguments);
  };

  // Resume after a navigation within the same session.
  checkNavigationSignals();
  startTimer();
})();
